import { Image, Text, View } from '@react-pdf/renderer'
import {
  Alert,
  Button,
  Card,
  Image as AntImage,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Space,
  Typography,
} from 'antd'
import {
  AssetVizMode,
  AssetVizSceneConfig,
  ClipConfig,
  GetSectionable,
} from 'features/assetViz'
import {
  CommercialFdeCdfSpecificationList,
  determineDefaultColorScaleThreshold,
  useCdfDataFetcher,
  useInitializeDerivedCdfsFromRolodexData,
} from 'features/dataLayers'
import { useRolodexEdit } from 'features/fde'
import { useGeckoLocale } from 'features/locale'
import sortBy from 'lodash/sortBy'
import { AssetVizCopy } from 'pages/reporting/components/AssetVizCopy'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import { ImagePageHeader } from 'pages/reporting/reports/rug/components/ImagePageHeader'
import {
  LAYOUT,
  MATH_CONSTANTS,
  REPORT_COLORS,
  SPACING,
  TYPOGRAPHY,
} from 'pages/reporting/reports/rug/constants/rugReportStyles'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { ComponentId, RugSectionStats } from 'pages/reporting/reports/rug/types'
import { captureAssetScreenshot } from 'pages/reporting/reports/rug/utils/captureAssetScreenshot'
import { getBoundsFromSectionable } from 'pages/reporting/reports/rug/utils/getBoundsFromSectionabe'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'pages/reporting/shared/pdfGeneration/components/shared/ReportPage'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentDataFrame,
  GeckoComponentShape,
  GeckoComponentSpace,
  GeckoFeature,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  isInspectionThicknessCDF,
  Section,
} from 'types'

const { Text: AntText } = Typography

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const ComponentSelector = styled.div`
  margin-bottom: 1rem;
`

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ImageCard = styled(Card)`
  margin-bottom: 1rem;

  .ant-image {
    background-color: #ffffff;
  }
`

interface SectionScreenshot {
  componentId: string
  componentName: string
  sectionIdx: number
  sectionName: string
  signedUrl: string
  imagePath: string
}

// PDF Styles
const tableStyles = {
  container: {
    marginTop: SPACING.MARGIN.TINY,
    flexDirection: 'column' as const,
    gap: 0,
  },
  measurementsColumn: {
    flex: LAYOUT.FLEX.NARROW,
  },
  commentsColumn: {
    marginTop: SPACING.MARGIN.TINY,
  },
  tableRow: {
    flexDirection: 'row' as const,
    borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
  },
  tableCell: {
    padding: SPACING.PADDING.EXTRA_SMALL,
    borderRight: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
  },
  tableCellWide: {
    flex: LAYOUT.FLEX.WIDE,
  },
  tableCellNarrow: {
    flex: LAYOUT.FLEX.NARROW,
  },
  tableCellLast: {
    borderRight: 'none',
  },
  headerText: {
    fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
    color: REPORT_COLORS.DARKER_GRAY,
  },
  cellText: {
    fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
  },
}

const pageStyles = {
  imagePage: {
    flexDirection: 'column' as const,
    padding: SPACING.PADDING.MEDIUM,
    paddingBottom: SPACING.PADDING.LARGE,
    fontSize: TYPOGRAPHY.FONT_SIZE.MEDIUM,
  },
}

// Helper function to format location strings
function formatLocation(location?: string): string | undefined {
  return location?.replace(/″/g, '"')?.replace(/′/g, "'")
}

// Table Row Component
const TableRow: React.FC<{
  label: string
  value: string | undefined
  locationX?: string
  locationY?: string
  mergeCells?: boolean
  isLastMergedRow?: boolean
}> = ({
  label,
  value,
  locationX,
  locationY,
  mergeCells = false,
  isLastMergedRow = false,
}) => (
  <View style={tableStyles.tableRow}>
    <View style={[tableStyles.tableCell, tableStyles.tableCellWide]}>
      <Text
        style={[
          tableStyles.cellText,
          { color: REPORT_COLORS.GECKO_BRIGHT_GREEN },
        ]}>
        {label}
      </Text>
    </View>
    <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
      <Text
        style={[
          tableStyles.cellText,
          { color: REPORT_COLORS.GECKO_BLUE_GREEN },
        ]}>
        {value}
      </Text>
    </View>
    {mergeCells ? (
      <View
        style={[
          tableStyles.tableCell,
          { flex: LAYOUT.FLEX.DOUBLE },
          tableStyles.tableCellLast,
          isLastMergedRow ? { borderBottom: 'none' } : {},
        ]}
      />
    ) : (
      <>
        {locationX && (
          <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
            <Text
              style={[
                tableStyles.cellText,
                { color: REPORT_COLORS.GECKO_BLUE_GREEN },
              ]}>
              {locationX}
            </Text>
          </View>
        )}
        {locationY && (
          <View
            style={[
              tableStyles.tableCell,
              tableStyles.tableCellNarrow,
              tableStyles.tableCellLast,
            ]}>
            <Text
              style={[
                tableStyles.cellText,
                { color: REPORT_COLORS.GECKO_BLUE_GREEN },
              ]}>
              {locationY}
            </Text>
          </View>
        )}
      </>
    )}
  </View>
)

// Section Stats Table Component
const SectionStatsTable: React.FC<{
  section: Section
  sectionStats?: RugSectionStats
  nominalThickness?: GeckoMeasurement
  relevantCDF?: GeckoComponentDataFrame
}> = ({ sectionStats, nominalThickness, relevantCDF }) => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const { formatMeasurement } = useRugReportOps()

  const { userFacingThicknessUnit, userFacingXUnit, userFacingYUnit } =
    useMemo(() => {
      const thicknessUnit = relevantCDF?.data.params.measurement_unit
      const xUnit = relevantCDF?.data.geometry.x.unit
      const yUnit = relevantCDF?.data.geometry.y.unit

      return {
        userFacingThicknessUnit: thicknessUnit
          ? getPreferredMeasurementUnit(thicknessUnit)
          : undefined,
        userFacingXUnit: xUnit ? getPreferredMeasurementUnit(xUnit) : undefined,
        userFacingYUnit: yUnit ? getPreferredMeasurementUnit(yUnit) : undefined,
      }
    }, [relevantCDF, getPreferredMeasurementUnit])

  const { minThicknesses, averageThickness } = useMemo(() => {
    const stats = sectionStats ?? {
      minThicknesses: [],
      averageThickness: GeckoMeasurement.CreateFromValue(
        Number.NaN,
        userFacingThicknessUnit ?? GeckoMeasurementUnit.None
      ),
    }
    return {
      minThicknesses: sortBy(
        stats.minThicknesses.map((minThickness) => ({
          thickness: minThickness.thickness.convertTo(userFacingThicknessUnit),
          location:
            userFacingXUnit && userFacingYUnit
              ? minThickness.location.project(
                  GeckoComponentSpace.CreateFromMeasurementUnits(
                    minThickness.location.componentID,
                    userFacingXUnit,
                    userFacingYUnit
                  )
                )
              : minThickness.location,
        })),
        (thickness) =>
          thickness.thickness.toNumber(
            userFacingThicknessUnit ?? thickness.thickness.unit
          )
      ),
      averageThickness: stats.averageThickness.convertTo(
        userFacingThicknessUnit
      ),
    }
  }, [userFacingThicknessUnit, sectionStats, userFacingXUnit, userFacingYUnit])

  const sectionNominalThickness = nominalThickness?.convertTo(
    userFacingThicknessUnit
  )

  const maxWallLoss =
    sectionNominalThickness &&
    minThicknesses.length > 0 &&
    userFacingThicknessUnit
      ? (1 -
          Math.min(
            ...minThicknesses.map((minThickness) =>
              minThickness.thickness.toNumber(userFacingThicknessUnit)
            )
          ) /
            sectionNominalThickness.toNumber(userFacingThicknessUnit)) *
        MATH_CONSTANTS.PERCENTAGE_SCALE
      : undefined

  const totalDataRows = 3 + minThicknesses.length

  return (
    <View style={tableStyles.container}>
      <View style={tableStyles.tableRow}>
        <View
          style={[
            tableStyles.tableCell,
            {
              flex: LAYOUT.FLEX.TRIPLE_HALF,
              borderRight: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
              borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            },
          ]}>
          <Text
            style={[
              tableStyles.headerText,
              { color: REPORT_COLORS.GECKO_BLUE_GREEN },
            ]}>
            Measurements
          </Text>
        </View>
        <View
          style={[
            tableStyles.tableCell,
            {
              flex: LAYOUT.FLEX.NARROW,
              borderRight: 'none',
              borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            },
          ]}>
          <Text style={tableStyles.headerText}>Comments</Text>
        </View>
      </View>

      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: LAYOUT.FLEX.TRIPLE_HALF }}>
          <TableRow
            label='Nominal Thickness'
            value={formatMeasurement({
              measurement: sectionNominalThickness,
              variant: 'narrow',
            })}
            locationX='Location-X'
            locationY='Location-Y'
          />

          {minThicknesses.map((minThickness, index) => (
            <TableRow
              key={index}
              label={`Minimum Thickness ${index + 1}`}
              value={formatMeasurement({
                measurement: minThickness.thickness,
                variant: 'narrow',
              })}
              locationX={formatLocation(
                formatMeasurement({
                  measurement: minThickness.location.x,
                  variant: 'geometric',
                })
              )}
              locationY={formatLocation(
                formatMeasurement({
                  measurement: minThickness.location.y,
                  variant: 'geometric',
                })
              )}
            />
          ))}

          <View
            style={[
              tableStyles.tableRow,
              {
                borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
              },
            ]}>
            <View style={[tableStyles.tableCell, tableStyles.tableCellWide]}>
              <Text
                style={[
                  tableStyles.cellText,
                  { color: REPORT_COLORS.GECKO_BRIGHT_GREEN },
                ]}>
                Max Wall Loss
              </Text>
            </View>
            <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
              <Text
                style={[
                  tableStyles.cellText,
                  { color: REPORT_COLORS.GECKO_BLUE_GREEN },
                ]}>{`${maxWallLoss?.toFixed(MATH_CONSTANTS.FIXED_DECIMAL_PLACES)} %`}</Text>
            </View>
            <View
              style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}
            />
            <View
              style={[
                tableStyles.tableCell,
                tableStyles.tableCellNarrow,
                tableStyles.tableCellLast,
              ]}
            />
          </View>

          <View style={[tableStyles.tableRow, { borderBottom: 'none' }]}>
            <View style={[tableStyles.tableCell, tableStyles.tableCellWide]}>
              <Text
                style={[
                  tableStyles.cellText,
                  { color: REPORT_COLORS.GECKO_BRIGHT_GREEN },
                ]}>
                Average Thickness
              </Text>
            </View>
            <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
              <Text
                style={[
                  tableStyles.cellText,
                  { color: REPORT_COLORS.GECKO_BLUE_GREEN },
                ]}>
                {formatMeasurement({
                  measurement: averageThickness,
                  variant: 'narrow',
                })}
              </Text>
            </View>
            <View
              style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}
            />
            <View
              style={[
                tableStyles.tableCell,
                tableStyles.tableCellNarrow,
                tableStyles.tableCellLast,
              ]}
            />
          </View>
        </View>

        <View
          style={{
            flex: LAYOUT.FLEX.NARROW,
            borderLeft: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            minHeight: totalDataRows * 20,
          }}
        />
      </View>
    </View>
  )
}

const RenderReact: FC = () => {
  const {
    selectedComponents,
    relevantCDFs,
    selectedAssetModel,
    assetViz,
    metadataEntryId,
    selectedComponentModels,
    assetScreenshots,
    sectionsByComponent,
    forceRefreshAssetScreenshots,
    unit,
    cdfCache,
    componentDefaults,
  } = useRugReportOps()

  const { assetVizRef } = assetViz || {}
  const [selectedComponentId, setSelectedComponentId] = useState<
    ComponentId | undefined
  >()
  const [selectedCDFId, setSelectedCDFId] = useState<string | undefined>()
  const [capturingSection, setCapturingSection] = useState(false)
  const [currentSectionIdx, setCurrentSectionIdx] = useState<number>(0)
  const [deletingImageKey, setDeletingImageKey] = useState<string | null>(null)
  const [zoomFactor, setZoomFactor] = useState<number>(0.15)
  const [sceneConfig, setSceneConfig] = useState<AssetVizSceneConfig>(() => ({
    ...assetViz.assetVizSceneConfig,
    clipConfig: new ClipConfig(),
  }))

  const [, setSelection] = useState<
    GeckoComponentShape | GeckoMarkupLayer | GeckoMarkupPrimitive | GeckoFeature
  >()

  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()

  const selectedComponent = useMemo(() => {
    if (!selectedComponentId || !selectedComponents) return undefined
    return selectedComponents.find((c) => c.id === selectedComponentId)
  }, [selectedComponentId, selectedComponents])

  const hasSections = useMemo(() => {
    if (!selectedComponent) return false
    const sectionable = sectionsByComponent.get(
      selectedComponent.id as ComponentId
    )
    return (
      sectionable && sectionable.sections && sectionable.sections.length > 0
    )
  }, [selectedComponent, sectionsByComponent])

  // Get the selected component model and its sections
  const { sections, sectionable } = useMemo(() => {
    if (!selectedComponent) {
      return {
        sections: [],
        sectionable: undefined,
      }
    }

    const sectionable = sectionsByComponent.get(
      selectedComponent.id as ComponentId
    )

    return { sections: sectionable?.sections ?? [], sectionable }
  }, [selectedComponent, sectionsByComponent])

  const componentCDFs = useMemo(() => {
    if (!selectedComponent || !relevantCDFs) return []
    return relevantCDFs.filter(
      (cdf) => cdf.data.geometry.componentID === selectedComponent.id
    )
  }, [selectedComponent, relevantCDFs])

  const relevantCDF = useMemo(() => {
    if (!selectedComponent || componentCDFs.length === 0) return undefined
    if (selectedCDFId) {
      return componentCDFs.find((cdf) => cdf.id === selectedCDFId)
    }
    return componentCDFs[0]
  }, [selectedComponent, componentCDFs, selectedCDFId])

  useEffect(() => {
    if (selectedComponent && componentCDFs.length > 0) {
      if (
        !selectedCDFId ||
        !componentCDFs.find((cdf) => cdf.id === selectedCDFId)
      ) {
        setSelectedCDFId(componentCDFs[0].id)
      }
    } else {
      setSelectedCDFId(undefined)
    }
  }, [selectedComponent, componentCDFs, selectedCDFId])

  // Reset clip config when component changes to avoid stale clipping from previous component
  useEffect(() => {
    if (selectedComponent) {
      setSceneConfig((prev) => ({
        ...prev,
        clipConfig: new ClipConfig(),
      }))
      setCurrentSectionIdx(0)
    }
  }, [selectedComponent])

  const colorThreshold = useMemo(() => {
    if (!relevantCDF) return undefined

    const selectedComponentDefault = componentDefaults.find(
      (cd) => cd.tags.component === selectedComponent?.id
    )
    const thresholdFromDefaults =
      selectedComponentDefault?.data.color_scale_thresholds.get(
        relevantCDF.data.params.view.id
      )

    return (
      thresholdFromDefaults ??
      determineDefaultColorScaleThreshold(
        relevantCDF.data.params.view,
        relevantCDF.data.params.measurement_unit,
        undefined
      )
    )
  }, [relevantCDF, componentDefaults, selectedComponent?.id])

  const existingScreenshots = useMemo(() => {
    const screenshots: SectionScreenshot[] = []

    if (assetScreenshots?.sectionHeatmap) {
      assetScreenshots.sectionHeatmap.forEach((screenshot) => {
        if (screenshot.signedUrl) {
          const component = selectedComponents?.find(
            (c) => c.id === screenshot.componentId
          )
          if (component) {
            // Find the section name
            const componentModel = selectedComponentModels?.find(
              (m) => m.id === screenshot.componentId
            )
            if (componentModel) {
              const sectionable = GetSectionable(componentModel)
              const section = sectionable?.sections?.[screenshot.sectionIdx]
              const sectionName = section?.name ?? component.data.name
              screenshots.push({
                componentId: screenshot.componentId,
                componentName: component.data.name,
                sectionIdx: screenshot.sectionIdx,
                sectionName: sectionName,
                signedUrl: screenshot.signedUrl,
                imagePath: screenshot.imagePath,
              })
            }
          }
        }
      })
    }

    return screenshots
  }, [assetScreenshots, selectedComponents, selectedComponentModels])

  const handleDeleteScreenshot = useCallback(
    async (screenshot: SectionScreenshot) => {
      const screenshotKey = `${screenshot.componentId}-${screenshot.sectionIdx}`
      setDeletingImageKey(screenshotKey)
      try {
        await rolodexDeleteFile(metadataEntryId, screenshot.imagePath)
        message.success('Screenshot deleted successfully')
        await forceRefreshAssetScreenshots()
      } catch (error) {
        console.error('Failed to delete section screenshot:', error)
        message.error('Failed to delete screenshot')
      } finally {
        setDeletingImageKey(null)
      }
    },
    [metadataEntryId, rolodexDeleteFile, forceRefreshAssetScreenshots]
  )

  useEffect(() => {
    if (!sectionable || !sections) {
      return
    }

    if (currentSectionIdx >= sections.length) {
      return
    }

    const currSection = sections[currentSectionIdx]
    setSceneConfig((prev) => {
      const clipConfig = prev.clipConfig
      clipConfig.updateFromSection(sectionable, currSection)

      return {
        ...prev,
        clipConfig: clipConfig,
        show3D: false,
      }
    })
  }, [sectionable, sections, currentSectionIdx])

  const cdfDataFetcher = useCdfDataFetcher()
  const captureNextSection = useCallback(async () => {
    if (
      !assetVizRef?.current ||
      !selectedComponent ||
      !relevantCDF ||
      !sections ||
      !sectionable
    ) {
      return
    }

    if (currentSectionIdx >= sections.length) {
      setCapturingSection(false)
      setCurrentSectionIdx(0)
      message.success('All section screenshots captured successfully')

      await forceRefreshAssetScreenshots()
      return
    }

    const boundsForSection = getBoundsFromSectionable(
      selectedComponent.id as ComponentId,
      sectionable,
      relevantCDF.data.geometry
    )

    const selectedComponentModel = selectedComponentModels?.find(
      (componentModel) => componentModel.id === selectedComponent.id
    )

    try {
      const currSection = sections[currentSectionIdx]
      const sectionBounds = boundsForSection[currentSectionIdx]

      // pre-load data
      // TODO: Add an event handler onto AssetViz, so we can listen for when the component is fully rendered and capture the screenshot then.
      await cdfDataFetcher(relevantCDF)

      const blob = await captureAssetScreenshot({
        assetVizRef,
        bounds: sectionBounds,
        zoomFactor,
        // Adds a 1/2 second delay to prevent the screenshot from being captured before the component is fully rendered (e.g. color scales are being applied)
        preRenderDelay: 500,
        componentModel: selectedComponentModel,
      })

      if (blob) {
        const path = `asset-screenshot/sectionHeatmap/${selectedComponent.id}/${currentSectionIdx}.png`
        await rolodexUploadFile(metadataEntryId, blob, path)

        message.success(`Captured ${currSection.name}`)

        setCurrentSectionIdx(currentSectionIdx + 1)
      }
    } catch (error) {
      console.error('Failed to capture section screenshot:', error)
      message.error('Failed to capture section screenshot')
      setCapturingSection(false)
      setCurrentSectionIdx(0)
    }
  }, [
    assetVizRef,
    selectedComponent,
    relevantCDF,
    sections,
    sectionable,
    currentSectionIdx,
    cdfDataFetcher,
    zoomFactor,
    rolodexUploadFile,
    metadataEntryId,
    forceRefreshAssetScreenshots,
    selectedComponentModels,
  ])

  useEffect(() => {
    if (capturingSection && sections) {
      captureNextSection()
    }
  }, [capturingSection, currentSectionIdx, sections, captureNextSection])

  const captureFullComponent = useCallback(async () => {
    if (!assetVizRef?.current || !selectedComponent || !relevantCDF) {
      message.error('Unable to capture screenshot - missing required data')
      return
    }

    const selectedComponentModel = selectedComponentModels?.find(
      (componentModel) => componentModel.id === selectedComponent.id
    )

    if (!selectedComponentModel) {
      message.error('Unable to capture screenshot - missing component model')
      return
    }

    setSceneConfig((prev) => {
      const freshClipConfig = new ClipConfig()
      return {
        ...prev,
        clipConfig: freshClipConfig,
      }
    })

    setCapturingSection(true)
    try {
      const componentBounds = selectedComponentModel.componentBounds

      await cdfDataFetcher(relevantCDF)

      const blob = await captureAssetScreenshot({
        assetVizRef,
        bounds: componentBounds,
        zoomFactor,
        preRenderDelay: 500,
        componentModel: selectedComponentModel,
      })

      if (blob) {
        const path = `asset-screenshot/sectionHeatmap/${selectedComponent.id}/0.png`
        await rolodexUploadFile(metadataEntryId, blob, path)

        message.success(`Captured ${selectedComponent.data.name}`)
        await forceRefreshAssetScreenshots()
      }
    } catch (error) {
      console.error('Failed to capture component screenshot:', error)
      message.error('Failed to capture screenshot')
    } finally {
      setCapturingSection(false)
    }
  }, [
    assetVizRef,
    selectedComponent,
    relevantCDF,
    selectedComponentModels,
    cdfDataFetcher,
    zoomFactor,
    rolodexUploadFile,
    metadataEntryId,
    forceRefreshAssetScreenshots,
  ])

  const startCapture = useCallback(() => {
    if (!selectedComponent) {
      message.error('Please select a component')
      return
    }

    if (!hasSections) {
      captureFullComponent()
      return
    }

    if (!sections || sections.length === 0) {
      message.error('No sections available for the selected component')
      return
    }

    setCurrentSectionIdx(0)
    setCapturingSection(true)
  }, [selectedComponent, hasSections, sections, captureFullComponent])

  const componentOptions = useMemo(() => {
    if (!selectedComponents) return []
    return selectedComponents.map((component) => ({
      label: component.data.name,
      value: component.id,
    }))
  }, [selectedComponents])

  useInitializeDerivedCdfsFromRolodexData(
    cdfCache,
    CommercialFdeCdfSpecificationList,
    unit?.id ?? [],
    isInspectionThicknessCDF
  )

  const cdfOptions = useMemo(() => {
    return componentCDFs.map((cdf) => ({
      label: `${cdf.data.params.view.name}${cdf.data.params.aggregation.name ? ` (${cdf.data.params.aggregation.name})` : ''}`,
      value: cdf.id,
    }))
  }, [componentCDFs])

  const screenshotsByComponent = useMemo(() => {
    const grouped = new Map<string, SectionScreenshot[]>()

    existingScreenshots.forEach((screenshot) => {
      const existing = grouped.get(screenshot.componentId) || []
      existing.push(screenshot)
      grouped.set(screenshot.componentId, existing)
    })

    grouped.forEach((screenshots) => {
      screenshots.sort((a, b) => a.sectionIdx - b.sectionIdx)
    })

    return grouped
  }, [existingScreenshots])

  return (
    <Container>
      <ComponentSelector>
        <Space orientation='vertical' style={{ width: '100%' }}>
          <AntText strong>Select Component:</AntText>
          <Select
            style={{ width: '100%' }}
            placeholder='Select a component'
            value={selectedComponentId}
            onChange={setSelectedComponentId}
            options={componentOptions}
          />
          {selectedComponent && cdfOptions.length > 0 && (
            <>
              <AntText strong>Select CDF:</AntText>
              <Select
                style={{ width: '100%' }}
                placeholder='Select a CDF'
                value={selectedCDFId}
                onChange={setSelectedCDFId}
                options={cdfOptions}
              />
            </>
          )}
          <Space orientation='horizontal' align='center'>
            <AntText>Screenshot Zoom Factor:</AntText>
            <InputNumber
              precision={2}
              value={zoomFactor}
              min={0}
              step={0.05}
              onChange={(value) => setZoomFactor(value as number)}
              style={{ width: '120px' }}
            />
            <AntText type='secondary' style={{ fontSize: '12px' }}>
              (Proportion of whitespace padding)
            </AntText>
          </Space>
          {componentOptions.length === 0 && (
            <Alert message='No components available' type='warning' showIcon />
          )}
        </Space>
      </ComponentSelector>

      {selectedComponent && assetVizRef && (
        <>
          <AssetVizCopy
            assetVizRef={assetVizRef}
            components={[selectedComponent]}
            componentDataFrames={relevantCDF ? [relevantCDF] : undefined}
            markupLayers={[]}
            assetModel={selectedAssetModel}
            getColorThresholdForComponent={() => colorThreshold}
            customMarkupLayers={[]}
            sceneConfig={sceneConfig}
            mode={AssetVizMode.Screenshot}
            onSelection={setSelection}
            offscreenRender={true}
            showLegend={true}
          />

          <Space>
            <Button
              type='primary'
              onClick={startCapture}
              loading={capturingSection}
              disabled={!selectedComponent}>
              {capturingSection ? 'Capturing...' : 'Capture Screenshot'}
            </Button>
            {capturingSection && (
              <Button onClick={() => setCapturingSection(false)}>
                Stop Capture
              </Button>
            )}
          </Space>
        </>
      )}

      <PreviewSection>
        <Typography.Title level={4}>
          Captured Section Screenshots
        </Typography.Title>
        {Array.from(screenshotsByComponent.entries()).map(
          ([componentId, screenshots]) => {
            const component = selectedComponents?.find(
              (c) => c.id === componentId
            )
            return (
              <div key={componentId}>
                <Typography.Title level={5}>
                  {component?.data.name}
                </Typography.Title>
                {screenshots.map((screenshot) => {
                  const screenshotKey = `${screenshot.componentId}-${screenshot.sectionIdx}`
                  return (
                    <ImageCard
                      key={screenshotKey}
                      title={screenshot.sectionName}>
                      <AntImage
                        src={screenshot.signedUrl}
                        alt={`${screenshot.componentName} - ${screenshot.sectionName}`}
                        style={{ width: '100%', maxWidth: '600px' }}
                      />
                      <Popconfirm
                        title='Delete screenshot?'
                        description='This will remove the screenshot from the report.'
                        okText='Delete'
                        cancelText='Cancel'
                        okButtonProps={{
                          danger: true,
                          loading: deletingImageKey === screenshotKey,
                        }}
                        onConfirm={() => handleDeleteScreenshot(screenshot)}>
                        <Button
                          danger
                          loading={deletingImageKey === screenshotKey}
                          style={{ marginTop: '0.75rem' }}>
                          Delete Screenshot
                        </Button>
                      </Popconfirm>
                    </ImageCard>
                  )
                })}
              </div>
            )
          }
        )}
      </PreviewSection>
    </Container>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    selectedComponents,
    assetScreenshots,
    selectedComponentModels,
    statsBySection,
    componentToNominalRange,
    relevantCDFs,
  } = useRugReportOps()

  const screenshots = useMemo(() => {
    const shots: SectionScreenshot[] = []

    if (assetScreenshots?.sectionHeatmap) {
      assetScreenshots.sectionHeatmap.forEach((screenshot) => {
        if (screenshot.signedUrl) {
          const component = selectedComponents?.find(
            (c) => c.id === screenshot.componentId
          )
          if (component) {
            // Find the section name
            const componentModel = selectedComponentModels?.find(
              (m) => m.id === screenshot.componentId
            )
            if (componentModel) {
              const sectionable = GetSectionable(componentModel)
              const section = sectionable?.sections?.[screenshot.sectionIdx]
              if (section) {
                shots.push({
                  componentId: screenshot.componentId,
                  componentName: component.data.name,
                  sectionIdx: screenshot.sectionIdx,
                  sectionName: section.name,
                  signedUrl: screenshot.signedUrl,
                  imagePath: screenshot.imagePath,
                })
              }
            }
          }
        }
      })
    }

    return sortBy(shots, ['componentId', 'sectionIdx'])
  }, [assetScreenshots, selectedComponents, selectedComponentModels])

  if (screenshots.length === 0) return null

  return (
    <>
      {screenshots.map((screenshot, index) => {
        const tracker =
          index === 0
            ? {
                sectionId: RugReportSectionId.RUG_DATA_IMAGES,
                onRender,
              }
            : undefined

        return (
          <ReportPage
            key={`${screenshot.componentId}-${screenshot.sectionIdx}`}
            id={index === 0 ? 'rug-data-images' : undefined}
            size='LETTER'
            orientation='landscape'
            pageStyle={pageStyles.imagePage}
            contentBottomPadding={20}
            tracker={tracker}
            header={<PageHeader title='RUG DATA IMAGES' />}
            footer={
              <PageFooter
                jobNumber={formData.mainInfo.assetId}
                revisionNumber={formData.revisionMetadata.revisionNumber}
              />
            }>
            <ImagePageHeader
              componentId={screenshot.componentId as ComponentId}
            />

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-start',
                marginBottom: 2,
                gap: 16,
              }}>
              <Text
                style={{
                  fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
                  color: REPORT_COLORS.GECKO_BLUE_GREEN,
                  fontWeight: 'bold',
                }}>
                {screenshot.sectionName}
              </Text>
            </View>

            {(() => {
              const componentStats = statsBySection?.get(
                screenshot.componentId as ComponentId
              )
              const sectionStats = componentStats?.get(screenshot.sectionName)
              const nominalRange = componentToNominalRange?.get(
                screenshot.componentId as ComponentId
              )
              const relevantCDF = relevantCDFs?.find((cdf) => {
                return cdf.data.geometry.componentID === screenshot.componentId
              })

              // Get the section object
              const componentModel = selectedComponentModels?.find(
                (m) => m.id === screenshot.componentId
              )
              const sectionable = componentModel
                ? GetSectionable(componentModel)
                : undefined
              const section = sectionable?.sections?.[screenshot.sectionIdx]

              return (
                <>
                  <Image
                    src={screenshot.signedUrl}
                    style={{
                      height: 'auto',
                      marginBottom: 2,
                    }}
                    cache={false}
                  />
                  {section && (
                    <SectionStatsTable
                      section={section}
                      sectionStats={sectionStats}
                      nominalThickness={nominalRange?.min}
                      relevantCDF={relevantCDF}
                    />
                  )}
                </>
              )
            })()}
          </ReportPage>
        )
      })}
      <AddendumPages
        sectionId={RugReportSectionId.RUG_DATA_IMAGES}
        sectionTitle='RUG Data Images'
      />
    </>
  )
}

export const RugDataImagesSection: ReportSection = {
  id: RugReportSectionId.RUG_DATA_IMAGES,
  title: 'RUG Data Images',
  description: 'Section-by-section thickness visualizations',
  required: true,
  RenderReact,
  RenderPDF,
  requiredFields: [],
}
